import cn from 'classnames'
import { forwardRef, Ref, useEffect, useState } from 'react'
import { useEvent } from 'react-use'

import styles from './AssetsInput.module.scss'

import { FileState, useDropzoneUpload } from '~hooks/useDropzoneUpload'
import AssetsInputPreview from '~lib/ui-kit/AssetsInput/AssetsInputPreview'
import Row from '~lib/ui-kit/Row'
import Text from '~lib/ui-kit/Text'
import HorizontalAssetsPreview from '~modules/assetModules/elements/HorizontalAssetsPreview'

export type AssetsInputProps<TFile> = {
  accept?: string | string[]
  defaultValue?: unknown
  disabled?: boolean
  error?: string
  getName?: (file: TFile) => string
  getUrl?: (file: TFile) => File | string
  id?: string
  maxFiles?: number
  maxSize?: number
  minSize?: number
  name?: string
  onFilesStateChange?: (filesState: FileState[]) => void
  readOnly?: boolean
  value?: unknown
  onChange?: (value: unknown) => void
  onClickAsset?: (index: number) => void
  onUpload?: (files: File[]) => Promise<TFile[]>
  carouselView?: boolean
  detailed?: boolean
  hideAssets?: boolean
  onLoadingChange?: (value: boolean) => void
}

function AssetsInput<TFile = unknown>(
  {
    disabled,
    error,
    id,
    name,
    readOnly,
    onClickAsset,
    carouselView,
    detailed,
    onFilesStateChange,
    hideAssets,
    onLoadingChange,
    ...dzProps
  }: AssetsInputProps<TFile>,
  ref: Ref<HTMLInputElement>
) {
  const [dragActive, setDragActive] = useState(false)

  const { filesState, getInputProps, getRootProps, remove } = useDropzoneUpload({
    disabled: disabled || readOnly,
    multiple: true,
    noKeyboard: true,
    noClick: true,
    ...dzProps,
  })

  useEvent('dragover', () => {
    if (!disabled && !readOnly) {
      setDragActive(true)
    }
  })

  useEvent('dragleave', () => {
    setDragActive(false)
  })

  useEvent('drop', () => {
    setDragActive(false)
  })

  useEffect(() => {
    if (onFilesStateChange) {
      onFilesStateChange(filesState)
    }
  }, [filesState, onFilesStateChange])

  useEffect(() => {
    const loading = filesState.some((file) => file.loading)
    if (onLoadingChange) {
      onLoadingChange(loading)
    }
  }, [filesState, onLoadingChange])

  return (
    <>
      <div {...getRootProps({ className: cn(styles.root, dragActive && styles.active) })} />
      <input {...getInputProps({ ref, id, name })} />
      {!hideAssets && carouselView && (
        <HorizontalAssetsPreview>
          {filesState.map((file, idx) => (
            <AssetsInputPreview
              key={idx}
              disabled={disabled}
              index={idx}
              name={file.name}
              loading={file.loading}
              readOnly={readOnly}
              url={file.url}
              onClickAsset={onClickAsset}
              onRemove={remove}
            />
          ))}
        </HorizontalAssetsPreview>
      )}
      {!hideAssets && !carouselView && (
        <Row justify="start" wrap gutter="1">
          {filesState.map((file, idx) => (
            <AssetsInputPreview
              key={idx}
              disabled={disabled}
              index={idx}
              detailed={detailed}
              loading={file.loading}
              readOnly={readOnly}
              name={file.name}
              url={file.url}
              onClickAsset={onClickAsset}
              onRemove={remove}
              className={styles.asset}
            />
          ))}
        </Row>
      )}
      {error && (
        <Text className={styles.error} variant="danger" type="caption">
          {error}
        </Text>
      )}
    </>
  )
}

export default forwardRef(AssetsInput)
